// Amazon-style product gallery: thumbnails (click or mouse hover) select the slide shown in the main scroll-snap track.
class SMediaGallery extends HTMLElement {
  #controller;
  #frame = 0;
  #index = 0;
  #timer = 0;
  #hover = false;
  #touchUntil = 0;
  #visible = true;
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('[data-gallery-track]');

    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--show') return;
        this.#show(Number(event.source?.dataset.index));
      },
      { signal },
    );

    // Hovering a thumbnail with a mouse previews it, like the reference marketplace layout.
    this.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) this.#show(Number(thumb.dataset.index));
      },
      { signal },
    );

    track?.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => {
          const index = Math.round(Math.abs(track.scrollLeft) / Math.max(track.clientWidth, 1));
          this.#mark(index);
        });
      },
      { signal, passive: true },
    );

    this.#autoplay();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  // Autoplay: advance every data-autoplay ms; any manual selection restarts the countdown.
  // Pauses on mouse hover, keyboard focus, recent touch, an open zoom dialog, off-screen, hidden tab,
  // or a video/3D slide; disabled under reduced motion.
  #autoplay() {
    const delay = Number(this.dataset.autoplay || 0);
    const slides = this.querySelector('[data-gallery-track]')?.children.length || 0;
    if (!delay || slides < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const { signal } = this.#controller;
    this.#hover = false;
    this.#touchUntil = 0;
    this.#visible = true;

    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.#hover = true; }, { signal });
    this.addEventListener('pointerleave', () => { this.#hover = false; this.#restart(); }, { signal });
    this.addEventListener('touchstart', () => { this.#touchUntil = Date.now() + 8000; }, { signal, passive: true });
    this.#observer = new IntersectionObserver((entries) => { this.#visible = entries[0].isIntersecting; });
    this.#observer.observe(this);
    this.#restart();
  }

  #restart() {
    clearTimeout(this.#timer);
    const delay = Number(this.dataset.autoplay || 0);
    if (!delay || !this.#observer) return;
    this.#timer = setTimeout(() => this.#advance(), delay);
  }

  #advance() {
    const track = this.querySelector('[data-gallery-track]');
    const slides = track?.children.length || 0;
    const current = track?.children[this.#index];
    const blocked =
      this.#hover ||
      Date.now() < this.#touchUntil ||
      !this.#visible ||
      document.hidden ||
      this.querySelector(':focus-visible') ||
      document.querySelector('dialog[open]') ||
      current?.querySelector('video, iframe, model-viewer, deferred-media');
    if (!blocked && slides > 1) this.#show((this.#index + 1) % slides);
    this.#restart();
  }


  #show(index) {
    const track = this.querySelector('[data-gallery-track]');
    const slide = track?.children[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slide.offsetLeft * (track.scrollLeft < 0 ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
    this.#mark(index);
  }

  #mark(index) {
    if (index !== this.#index) {
      this.#index = index;
      this.#restart();
    }
    this.querySelectorAll('[data-thumb]').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-media-gallery')) customElements.define('s-media-gallery', SMediaGallery);
